import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import selectors from '@/redux/selectors/selectors';
import { query } from '@/redux/selectors/querySelectors';
import getLogTimeMachine from './lib/getLogTimeMachine';
import getAllStepIterations from './lib/getAllStepIterations';
import WorkflowOutputPaneUtilities from '../components/WorkflowOutputPaneUtilities';
import OutputErrorViewer from '../workflowEditor/workflowEditorPanels/primaryPanels/outputPanel/OutputErrorViewer';
import OutputLogViewer from './logStepOutput/OutputLogViewer';
import { FiBarChart, FiFilter, FiScissors, FiSlash } from '@/components/react-icons/main/index.esm';
import EmptyStateNotice from '@/components/EmptyStateNotice';
import ScrollContainerV2 from '@/components/v2/ScrollContainerV2';
import Tag from '@/components/Tag.jsx';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';

const postProcessingIconDefs = [
  { id: 'filter', icon: FiFilter, title: 'Records were filtered.' },
  { id: 'sort', icon: FiBarChart, title: 'Records were sorted.' },
  { id: 'slice', icon: FiScissors, title: 'Records were sliced.' }
];

const LogStepOutput = ({ invokedBy, manualUser, manualUserId }) => {
  const activeLogId = useArgSelector(query, 'logId');
  const activeLog = useArgSelector(selectors.workflowLogsById, activeLogId);
  const time = useArgSelector(query, 'time');
  const activeStepId = useArgSelector(query, 'logStepId');
  const output = useArgSelector(selectors.workflowLogOutputById, activeLogId);
  const startedOn = useSelector((state) => selectors.workflowLogsById(state, activeLogId)?.startedOn);
  const [search, setSearch] = useState('');

  const triggerStep = activeLog?.meta?.triggerStep;
  const operationLogs = useArgSelector(selectors.workflowOperationLogsByLogId, activeLogId);
  const isCrashed = activeLog?.statusId === 5;
  const isCancelled = activeLog?.statusId === 4;
  const isRunning = activeLog?.startedOn && !activeLog?.finishedOn && activeLog?.statusId === 1;
  const { allSteps } = useMemo(() => {
    return getLogTimeMachine({ output, operationLogs, time, triggerStep, invokedBy, manualUser, manualUserId, incomplete: isCancelled || isCrashed });
  }, [output, operationLogs, time, triggerStep, invokedBy, manualUser, manualUserId, isCancelled, isCrashed]);
  const stepOutput = allSteps?.find((n) => n.stepId === activeStepId)?.lastOutput;
  const stepMessage = useMemo(() => {
    if (activeStepId === 'manual-trigger') {
      return 'Manual Run';
    }
    if (activeStepId === 'function-trigger') {
      return `Ran from Workflow`;
    }
    return output?.meta?.steps?.find((n) => n.id === activeStepId)?.message;
  }, [activeStepId]);

  // Collect all iterations of this step if it's inside a loop
  const allStepIterations = useMemo(() => {
    if (!output?.data || !activeStepId) {
      return [];
    }
    return getAllStepIterations(output.data, activeStepId);
  }, [output?.data, activeStepId]);

  const hasMultipleIterations = allStepIterations.length > 1;

  const isSkipped = stepOutput?.ran === false;
  const success = stepOutput?.response?.success;
  const isError = !isSkipped && !success;
  const isAuthActionError = !success && stepOutput?.response?.meta?.action?.isAuthAction === true;
  const elapsed = (new Date(stepOutput?.finishedOn) - new Date(startedOn)) / 1000 || 0;
  const total = stepOutput?.response?.meta?.output?.total || stepOutput?.response?.data?.length;
  const totalRecords = stepOutput?.response?.meta?.pagination?.total;
  const responseIsEmpty = total === 0;
  const postProcessingData = stepOutput?.response?.meta?.postProcessing;
  const postProcessingIcons = postProcessingIconDefs.filter((def) => postProcessingData?.operations?.includes(def.id));
  const outputRetries = stepOutput?.response?.meta?.retries;

  // Need to do this for the Default error component. It expects the request to be inside the response, but with logs it's not (we're cleaning it up from response during formatWorkflowHistory) to avoid duplicate data.
  const responseWithRequest = { ...(stepOutput?.response || {}), meta: { ...(stepOutput?.response?.meta || {}), request: stepOutput?.request } };

  // Client-side search filtering for log data (the working OutputPane uses server-side filtering via getStepActionResponses, but log data is static)
  const searchFilteredResponse = useMemo(() => {
    if (!search || !Array.isArray(responseWithRequest?.data)) {
      return responseWithRequest;
    }
    const lowerSearch = search.toLowerCase();
    const filtered = responseWithRequest.data.filter((item) => JSON.stringify(item).toLowerCase().includes(lowerSearch));
    return { ...responseWithRequest, data: filtered };
  }, [responseWithRequest, search]);

  const breadcrumbStack = useMemo(() => {
    const stack = [];

    if (outputRetries > 0) {
      stack.push({ label: `Attempt ${outputRetries}`, className: 'retry-stack', retryStack: stepOutput?.response?.meta?.retryStack });
    }
    if (isError) {
      stack.push({ label: isAuthActionError ? 'Error Authenticating' : 'Error', className: 'error' });
      return stack;
    }
    if (responseIsEmpty) {
      stack.push({ label: 'No Data' });
      return stack;
    }

    if (postProcessingData) {
      if (postProcessingData.originalLength !== postProcessingData.length) {
        stack.push({ label: `${postProcessingData.originalLength} Results` });
      }
      if (postProcessingIcons?.length > 0) {
        stack.push({ icons: postProcessingIcons });
      }
    }

    if (total) {
      const prefix = 'Filtered to';
      const dataDiff = postProcessingData && postProcessingData.length !== postProcessingData.originalLength;
      const label = `${total} Result${total === 1 ? '' : 's'}${totalRecords ? ` of ${totalRecords}` : ''}`;
      const tooltip = dataDiff ? `The Transform Response settings changed the number of results from ${postProcessingData.originalLength} to ${postProcessingData.length}` : undefined;

      stack.push({ label: search ? `${prefix} ${label}` : label, tooltip });
    }
    stack.push({ label: 'Standard View', className: 'output-view-mode-trigger' });
    return stack;
  }, [responseIsEmpty, isError, isAuthActionError, total, search, postProcessingData]);

  if (!stepOutput || isRunning) {
    return null;
  }

  if (isSkipped) {
    return (
      <ScrollContainerV2 className={'log-step-output'}>
        <div className="log-step-output-header">
          <div className={'log-section-title'}>{stepMessage}</div>
        </div>
        <EmptyStateNotice //
          icon={FiSlash}
          size={'sm'}
          title={'Step skipped'}
          description={'This step is configured to be skipped, so it did not run during this execution. To run it, un-skip the step in the workflow editor.'}
        />
      </ScrollContainerV2>
    );
  }

  return (
    <ScrollContainerV2 className={'log-step-output'}>
      <div className="log-step-output-header">
        <div className={'log-section-title'}>{stepMessage}</div>
        <div className={'log-section-elapsed'}>
          <Tag title={`This step completed ${elapsed} seconds after the workflow started.`}>{elapsed}s</Tag>
        </div>
      </div>
      <WorkflowOutputPaneUtilities search={search} onChange={setSearch} output={responseWithRequest} stepId={stepOutput?.stepId} breadcrumb={breadcrumbStack} isLog={true} allStepIterations={hasMultipleIterations ? allStepIterations : undefined} />
      {success && <OutputLogViewer output={searchFilteredResponse} search={search} />}
      {!success && <OutputErrorViewer output={responseWithRequest} />}
    </ScrollContainerV2>
  );
};

export default LogStepOutput;
